import { ApiClient } from "~/api/api-client";
import { clarityHeaderValue } from "~/lib/tracking/clarity-ids";

import { isAttemptId } from "./attempt-id";

import type { AuthSurface, BotEventName, TurnstileTier } from "./types";

/**
 * Lo que el navegador le cuenta a la API del antibot y que puede no terminar nunca en un pedido
 * de código: escaló al nivel con clic, lo resolvió, el widget dio error, el script no cargó, o la
 * persona se fue mientras esperaba. Sin esto, quien abandona en la comprobación no deja rastro.
 *
 * Va por `navigator.sendBeacon` (sobrevive a cerrar la pestaña) con el JSON como texto plano, que
 * es lo único que un beacon manda sin pedir permiso (CORS); la API lo acepta así. Nunca tira:
 * medir no puede romper un formulario.
 */

export type BotEvent = {
  event: BotEventName;
  attemptId?: string;
  surface?: AuthSurface;
  tier?: TurnstileTier;
  /** Código de error de Cloudflare o motivo propio. La API acepta hasta 64 caracteres. */
  clientError?: string | null;
  waitMs?: number | null;
};

const CLIENT_ERROR_MAX = 64;
const BOT_EVENT_PATH = "/api/auth/bot-event";

function botEventUrl() {
  let base = "";
  try {
    base = ApiClient.instance.defaults.baseURL ?? "";
  } catch {
    // Cliente mockeado o sin inicializar: mismo origen.
  }
  return `${base.replace(/\/+$/, "")}${BOT_EVENT_PATH}`;
}

/** El body tal como lo valida la API: lo que no cumple se omite en vez de invalidar el evento. */
export function botEventBody(event: BotEvent): Record<string, unknown> {
  const body: Record<string, unknown> = { event: event.event };
  if (isAttemptId(event.attemptId)) body.attemptId = event.attemptId;
  if (event.surface) body.surface = event.surface;
  if (event.tier) body.tier = event.tier;
  if (event.clientError) body.clientError = String(event.clientError).slice(0, CLIENT_ERROR_MAX);
  if (typeof event.waitMs === "number" && Number.isFinite(event.waitMs)) {
    body.waitMs = Math.max(0, Math.round(event.waitMs));
  }
  // Un beacon no puede mandar el header X-Clarity: los ids van en el body.
  const clarity = clarityHeaderValue();
  if (clarity) body.clarity = clarity;
  return body;
}

export function reportBotEvent(event: BotEvent) {
  if (typeof window === "undefined") return;
  try {
    const url = botEventUrl();
    const payload = JSON.stringify(botEventBody(event));
    if (typeof navigator.sendBeacon === "function" && navigator.sendBeacon(url, payload)) return;
    // Sin sendBeacon (o con la cola llena) queda fetch con keepalive, que también sobrevive a
    // la navegación. Mismo tipo de contenido: así no hay preflight.
    void fetch(url, {
      method: "POST",
      body: payload,
      keepalive: true,
      credentials: "include",
      headers: { "content-type": "text/plain;charset=UTF-8" },
    }).catch(() => undefined);
  } catch {
    // Un evento que no salió no es problema de quien está completando el formulario.
  }
}
